<template>
  <el-dialog
    :visible="visible"
    :title="title"
    :width="width"
    append-to-body
    :close-on-click-modal="false"
    lock-scroll
    center
    @update:visible="$emit('update:visible', $event)"
    :class="{ 'custom-height-dialog': height !== '80%' }"
    :style="dialogStyle"
  >
    <template slot="title">
      <slot name="title">{{ title }}</slot>
    </template>
    <div
      class="dialog-scroll-wrapper d-flex flex-column"
      :class="{ 'custom-height': height !== '80%' }"
    >
      <slot />
    </div>
    <template slot="footer">
      <slot name="footer" />
    </template>
  </el-dialog>
</template>
<script>
export default {
  name: "AIVDialog",
  model: { prop: "visible", event: "update:visible" },
  props: {
    visible: { type: Boolean, required: true },
    title: { type: String, default: "" },
    width: { type: String, default: "90%" },
    height: { type: String, default: "80%" },
  },
  computed: {
    dialogStyle() {
      if (this.height !== "80%") {
        return {
          "--dialog-body-height": this.height,
        };
      }
      return {};
    },
  },
};
</script>
<style>
.dialog-scroll-wrapper {
  max-height: calc(100% - 60px);
  overflow-y: auto;
  padding-right: 1rem;
  padding-left: 1rem;
}

.dialog-scroll-wrapper.custom-height {
  max-height: calc(100% - 60px) !important;
  height: auto !important;
}

/* Main dialog structure */
/*noinspection CssUnusedSymbol */
.el-dialog {
  border-radius: 8px;
  overflow: hidden;
  background-color: #2c2c2c;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  margin-top: 5vh !important;
  margin-bottom: 1vh !important;
  /* Flex column so the header and footer stay pinned/visible and only the
     body shrinks and scrolls. Cap the whole dialog to the viewport with dvh
     (vh fallback first) — on mobile, vh counts the area behind the URL/toolbar
     chrome, so a vh-sized dialog can grow past the *visible* screen and clip
     the footer (e.g. the Clock In button), which then can't be scrolled to. */
  display: flex;
  flex-direction: column;
  max-height: 90vh !important;
  max-height: 90dvh !important;
}

/* Header and footer never shrink — the footer (action buttons) must always
   be visible. Only the body flexes/scrolls (see .el-dialog__body below). */
.el-dialog__header,
.el-dialog__footer {
  flex: 0 0 auto;
}

/* Custom height dialogs - allow a taller body, but still viewport-bounded.
   Previously this was max-height:none, which let tall dialogs grow past the
   screen and clip the footer on mobile. Keep them capped to the visible
   viewport so the footer stays reachable.
   NOTE: class/style bindings on <el-dialog> land on its root element,
   the .el-dialog__wrapper — so .custom-height-dialog must be matched
   as an ancestor of .el-dialog, never compounded with it. */
.custom-height-dialog .el-dialog {
  max-height: 94vh !important;
  max-height: 94dvh !important;
}

/*noinspection CssUnusedSymbol */
.el-dialog__header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

/*noinspection CssUnusedSymbol */
.el-dialog__header,
.el-dialog__body,
.el-dialog__footer {
  background-color: #3f3f3f;
  color: white;
  border: none;
  box-shadow: none;
  width: 100%;
  padding: 15px 0 15px !important;
}

/*noinspection CssUnusedSymbol */
.el-dialog__title {
  color: #e0e0e0;
  flex: 1;
  text-align: center;
}

/*noinspection CssUnusedSymbol */
.el-dialog__body {
  display: block !important;
  overflow-y: auto !important;
  padding: 20px !important;
  justify-content: stretch !important;
  /* The one flexible region: takes the space left between the pinned header
     and footer. min-height:0 lets it actually shrink so its own scrollbar
     (not the dialog) absorbs overflow, keeping the footer on-screen. */
  flex: 1 1 auto;
  min-height: 0;
  /* The dialog body is the modal's one scroll context. Contain the
     scroll so reaching its end doesn't chain-scroll the page behind
     the modal (the "modal won't scroll, background does" bug on touch
     devices), and keep touch scrolling smooth on iOS. */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* Element UI adds this class to <body> while a lock-scroll dialog is
   open — enforce it so nothing scrolls behind the modal */
body.el-popup-parent--hidden {
  overflow: hidden !important;
}

/* Swipes on the dimmed backdrop must not scroll the page behind it */
.v-modal {
  touch-action: none;
}

/* Default body height when no custom height is specified.
   The dialog itself is capped at 90vh with overflow:hidden, so the body's
   cap must leave room for BOTH the header and the footer — otherwise the
   footer (action buttons) is pushed past the 90vh cap and clipped, with no
   way to scroll to it. Reserve ~160px of chrome (header + footer + margins),
   mirroring the .custom-height-dialog body cap below. */
.el-dialog__body {
  max-height: calc(90vh - 160px) !important;
}

/* Custom height dialogs should not have max-height restrictions */
.custom-height-dialog .el-dialog__body {
  max-height: calc(var(--dialog-body-height, 85vh) - 150px) !important;
  height: auto !important;
}

/*noinspection CssUnusedSymbol */
.el-dialog__footer {
  display: flex !important;
  justify-content: center;
  padding: 15px 20px !important;
}

/*noinspection CssUnusedSymbol */
.el-dialog__wrapper {
  background-color: transparent;
}

/*noinspection CssUnusedSymbol */
.el-dialog .el-button {
  border-radius: 6px;
}

/* Override dialog centering behavior for better form layout */
.dialog-scroll-wrapper {
  max-height: calc(100% - 40px) !important;
  height: auto !important;
  display: flex !important;
  flex-direction: column !important;
  overflow-y: auto !important;
  align-items: stretch !important;
}

/* Custom height dialogs - adjust height appropriately */
.custom-height-dialog .dialog-scroll-wrapper {
  max-height: calc(100% - 40px) !important;
}

/* Force full width content inside dialogs */
.el-dialog__body > * {
  width: 100% !important;
  max-width: 100% !important;
}

/* Full width form support */
.full-width-form {
  width: 100%;
  padding: 0;
  margin: 0;
}

/* Ensure all field rows consistently fill the full width */
.row.mb-3 {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 1.5rem !important;
}

.row.mb-3 > div {
  padding-left: 8px;
  padding-right: 8px;
}

/* Override bootstrap column classes to ensure consistent field sizing */
.col-md-4 {
  flex: 1;
  max-width: none;
}

.col-md-6 {
  flex: 1;
  max-width: none;
}

.col-md-12 {
  flex: 1;
  max-width: 100% !important;
}

/* Make sure all input elements fill their container */
.w-100,
.el-input,
.el-input__inner {
  width: 100% !important;
}

/* Form layout styles - moved from global.css */
.el-form {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Content containers */
.row.col-md-12,
.row.col-md-10 {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
}

/* Row elements expand properly */
.row {
  width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Full width containers only */
.col-md-10 {
  max-width: 100% !important;
  flex: 0 0 100% !important;
}

/*noinspection CssUnusedSymbol */
@media (min-width: 768px) {
  .el-dialog__body .col-md-10 {
    flex: none !important;
  }
}

/* One scroll context per modal: the dialog body (max-height capped
   above) is THE scroller. This inner wrapper must never grow its own
   scrollbar — nested scrollbars made modals feel unscrollable,
   especially on touch devices. Declared last to win the cascade. */
.dialog-scroll-wrapper,
.dialog-scroll-wrapper.custom-height,
.custom-height-dialog .dialog-scroll-wrapper {
  max-height: none !important;
  overflow: visible !important;
}

/* Mobile: percentage widths passed by callers (width="50%" etc.) are
   meant for desktop — floor every dialog to near-full-screen, and let
   the side-by-side field columns (forced to flex:1 above, which
   defeats Bootstrap's own col-md-* stacking) wrap to one per row. */
@media (max-width: 768px) {
  .el-dialog {
    width: 95% !important;
    margin-top: 2vh !important;
  }

  .el-dialog .col-md-4,
  .el-dialog .col-md-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .dialog-scroll-wrapper {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}
</style>
